import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { getIncomers, Handle, NodeProps, Position, useEdges, useNodes, Node, getConnectedEdges, Background } from 'reactflow';
import { Typography, Box, Button, Divider, TextField, IconButton, Checkbox, Paper, Tooltip, lighten, useTheme } from '@mui/material';
import { EditorContext } from '../context';
import { ReactComponent as AssetIcon } from '../../../../assets/icons/main/plc-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/main/delete-icon.svg';
import { ReactComponent as ScanIcon } from '../../../../assets/ScanIcon.svg';
import { ReactComponent as AddCircleIcon} from '../../../../assets/icons/main/plus-circle-icon.svg';
import { ReactComponent as ConfigurationIcon } from '../../../../assets/ConfigurationIcon.svg';
import { ReactComponent as PlusCircleIcon } from '../../../../assets/PlusCircleIcon.svg';
import { ReactComponent as AlertIcon } from '../../../../assets/icons/main/alertIcon.svg';
import { ReactComponent as VrLine } from '../../../../assets/VrLine.svg';
import { CheckCircle, CopyAll, Delete, DoNotDisturb, LabelImportantOutlined, Remove } from '@mui/icons-material';
import Switch from "@mui/material/Switch";
import { LoadingButton } from "@mui/lab";
import * as uuid from "uuid";
import { AssetSchema, AssetUISchema, MachineSchema, MachineUISchema } from "../panel-schemas";
import { MachineNode } from "./Machine";
import { ParamsPanel } from "../panels/ParamsPanel";
import { has } from 'lodash';
import { palette } from '../../../../theme/palette';
import { useOutletContext } from 'react-router-dom';
import { get } from 'lodash';
import chroma from 'chroma-js';
import theme from '../../../../theme';

const Styles = {
  nodeWrapper: {
    height: '48px',
    position: 'relative',
    // border: '1px solid #D9DFED',
    borderRadius: '12px',
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '400px',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '11px 0 0 11px',
    backgroundColor:palette.info.main,
  },
  icon: {
    height: '48px',
    width: '48px',
  },
  handleSource: {
    height: '1px',
    width: '1px',
    backgroundColor: 'transparent',
    left: 0,
  },
  handleTarget: {
    height: '1px',
    width: '1px',
    backgroundColor: 'transparent',
    right: 0,
  },
  addIcon: {
    position: 'absolute',
    left: '-36px',
  },
  assetNodeWrapper: {
    width: '355px',
    borderRadius: '18px',
    borderBottom: '6px solid #82A6E0',
    // backgroundColor: '#FFF',
    boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2), -2px 0px 4px rgba(0, 0, 0, 0.2)',
  },
  header: {
    height: '79px',
    display: 'flex',
    alignItems: 'center',
    padding: '24px 24px 14px 24px',
    borderBottom: '1px dashed #82A6E0',
    boxSizing: 'border-box',
  },
  // titleWrapper: {
  //   display: 'flex',
  //   alignItems: 'baseline',
  // },
  title: { fontSize: '1.1rem', marginLeft: 1 },
  address: { fontSize: '0.8rem', marginLeft: 1, fontWeight: 400 },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding: '24px',
    gap: '16px',
  },
  scanContainer: {
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    backgroundColor: '#F8FAFB',
    padding: '24px 24px 8px 24px',
    boxSizing: 'border-box',
  },
  scanText: { fontSize: '1rem', fontWeight: 400, color: '#434D67' },
  scanWrapper: { display: 'flex', justifyContent: 'space-between', gap: '16px' },
  scanField: { width: '100%', height: '42px', flexShrink: 1 },
  btnsWrapper: { position: 'relative', display: 'flex', flexDirection: 'row', alignSelf: 'stretch' },
  btn: { width: 'auto', flexGrow: 1, height: '42px', color: '#64759F', borderColor: '#64759F' },
  scanBtn: {
    width: '109px',
    height: '42px',
    fontSize: '1rem',
    backgroundColor: '#82A6E0',
    flexShrink: 0,
    flexGrow: 1,
  },
  labelConnectedIcons: {
    width: 'calc(100% - 50px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  nodeLabel: {
    fontSize: '1.1rem',
    marginLeft: 1,
    width: '200px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
};


export const AssetNode: FunctionComponent<NodeProps> = ({ id, type, data, xPos, yPos }: NodeProps) => {
  const {
    onBtnClick,
    updateData,
    removeNode,
    editable,
    scanningTags,
    scanTags,
    duplicateNode,
    useCase,
    hasAccessTo
  } = useContext(EditorContext);
  const nodes = useNodes();
  const edges = useEdges();
  const thisNode: any = useMemo(() => nodes.find((node: Node) => node.id === id) as Node, [nodes]);
  const gateway: Node<any> | null = useMemo(() => thisNode ? getIncomers(thisNode, nodes, edges)[0] : null, [thisNode]);
  const connectedEdges = useMemo(() => getConnectedEdges([thisNode], edges), [thisNode, edges]);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const themeData: any = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});
  const [cardPalette, setCardPalette] = useState<any[]>([]);

  useEffect(() => {
    const _assets = nodes.filter((item: any) => { return item.type === "assetNode" });
    if (_assets.length) {
      const _filteredAssets = _assets.filter((items: any) => {
        if (items.connectedTo === thisNode.connectedTo) {
          if (items.data.name === thisNode.data.name || items.data.ip === thisNode.data.ip) {
            if (has(items.data, 'name') && has(items.data, 'ip')) {
              if (items.data.name.trim() != '' && items.data.ip.trim() != '') {
                return items
              }
            }
          }
        }
      })

      if ((_filteredAssets.length > 1)) {
        setIsDuplicate(true)
      } else {
        setIsDuplicate(false)
      }
    }
  }, [nodes, thisNode])

  const TooltipAssetComponent = (<Tooltip
    title={
      <Typography variant='subtitle1' style={{ fontSize: '0.8rem' }}>
        {isDuplicate ? 'Duplicate asset name or ip address found' : 'Please enter asset name and IP address.'}
      </Typography>
    }
    placement='left'
    arrow>
    <AlertIcon style={{ height: '24px', width: '24px', marginRight: '10px', color: palette.warning.main }} />
  </Tooltip>)

  const hasReadOnlyAccess = !(hasAccessTo.create && hasAccessTo.update)

  return (
    <>
      <Handle style={Styles.handleTarget} type="target" position={Position.Right} />
      <Handle style={Styles.handleSource} type="source" position={Position.Left} />
      <Paper elevation={0} sx={{ ...Styles.nodeWrapper,border:`1px solid ${themeData.palette.customBorderColor.main}`,
        // background: isDuplicate ? palette.warning.dark : (data.enabled) ? (data.ip && data.name) ? (data.name.trim() === '' || data.ip.trim() === '') ? palette.warning.dark : themeData.palette.background.paper : palette.warning.dark : themeData.palette.background.paper }}>
        background: isDuplicate ? palette.warning.dark : (data.enabled) ? (data.ip && data.name) ? (data.name.trim() === '' || data.ip.trim() === '') ? palette.warning.dark : ((themeMode.isLightBG) ? themeData.palette.background.paper : lighten(themeData.palette.background.default, 0.08)) : palette.warning.dark : ((themeMode.isLightBG) ? themeData.palette.background.paper : lighten(themeData.palette.background.default, 0.2))
      }}>
        {editable && <Tooltip title={'Link a machine'}>
          <IconButton sx={Styles.addIcon} onClick={() => onBtnClick('assetNode', {
            id: 'machine',
            type: 'machineNode',
            display: 'Machine',
            node: MachineNode,
            sourcePosition: Position.Left,
            targetPosition: Position.Right,
            connectedTo: id,
            panel: {
              custom: true,
              component: ParamsPanel,
              schema: MachineSchema,
              uiSchema: MachineUISchema,
            },
          })}
          disabled={hasReadOnlyAccess}><AddCircleIcon style={{ color: themeData.palette.custom.light, opacity: hasReadOnlyAccess ? '0.5' : 1 }} /></IconButton>
        </Tooltip>}
        <Box sx={Styles.iconWrapper}>
          <IconButton style={Styles.icon} onClick={() => onBtnClick('assetNode', { id, data })}><AssetIcon style={{ color: palette.primary.contrastText }} /></IconButton>
        </Box>
        <Typography sx={{ fontSize: '1.1rem', marginLeft: 1, textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isDuplicate ? TooltipAssetComponent : (data.enabled) ? (data.ip && data.name) ? (data.name.trim() === '' || data.ip.trim() === '') ? TooltipAssetComponent : '': TooltipAssetComponent : ''}
          {/* {data.ip ? `${data.name ?? ''} (${data.ip})` : 'Asset'} */}
          {data.name ? `${data.name ?? ''}  ${data.ip ? '(' +data.ip +')' : ''}` : 'Asset'}
        </Typography>
        <Box display={'flex'} alignItems={'center'} justifyContent={'end'}>
          {editable ? <IconButton disabled={!editable || hasReadOnlyAccess}
            onClick={() => removeNode(id)}><DeleteIcon style={{ color: themeData.palette.custom.light, opacity: hasReadOnlyAccess ? '0.5' : 1 }} /></IconButton> : <Box sx={{ width: 38 }} />}
        </Box>
      </Paper >
      {/*<Box sx={{...Styles.assetNodeWrapper}}>*/}
      {/*  <Box sx={Styles.header}>*/}
      {/*    {type === 'assetNode' && <Handle className={'asset-main'} type="target" position={Position.Left} id={`${id}-target`}/>}*/}
      {/*    {!useCase && <Switch checked={data.enabled} onClick={() => {*/}
      {/*      updateData({*/}
      {/*        force_update_id: id,*/}
      {/*        enabled: !data.enabled*/}
      {/*      });*/}
      {/*    }}/>}*/}
      {/*    <WifiIcon/>*/}
      {/*    <Box sx={Styles.titleWrapper}>*/}
      {/*      <Typography sx={Styles.title}>{data.label}</Typography>*/}
      {/*      /!*<Typography sx={Styles.address}>({data.name})</Typography>*!/*/}
      {/*    </Box>*/}
      {/*    /!*<IconButton style={{position: 'absolute', right: 48}} onClick={() => duplicateNode(thisNode, connectedEdges)}><CopyAll /></IconButton>*!/*/}
      {/*    <IconButton style={{position: 'absolute', right: 8}} onClick={() => removeNode(id)}><Delete/></IconButton>*/}
      {/*  </Box>*/}
      {/*  <Box sx={Styles.scanContainer}>*/}
      {/*    <Typography sx={Styles.scanText}>Asset Name</Typography>*/}
      {/*    <Box sx={Styles.scanWrapper}>*/}
      {/*      <TextField disabled={!data.enabled || !editable} value={data.name} onChange={(e) => updateData({name: e.target.value})}*/}
      {/*                 sx={Styles.scanField}/>*/}
      {/*    </Box>*/}
      {/*    <Typography sx={Styles.scanText}>IP Address</Typography>*/}
      {/*    <Box sx={Styles.scanWrapper}>*/}
      {/*      <TextField disabled={!data.enabled || !editable} value={data.ip} onChange={(e) => updateData({ip: e.target.value})}*/}
      {/*                 sx={Styles.scanField}/>*/}
      {/*    </Box>*/}
      {/*  </Box>*/}
      {/*  <Box sx={Styles.container}>*/}
      {/*    {(data.options || []).map(*/}
      {/*      (*/}
      {/*        option: {*/}
      {/*          id: string | undefined;*/}
      {/*          component_id: string | undefined;*/}
      {/*          component_name: string | undefined;*/}
      {/*          enabled: boolean*/}
      {/*        },*/}
      {/*        i: number*/}
      {/*      ) => (*/}
      {/*        <Box key={id} sx={Styles.btnsWrapper}>*/}
      {/*          /!* <Handle type="target" position={Position.Left} {...(data.handles && { id: data.handles[i] })} /> *!/*/}
      {/*          /!*<Handle type="target" position={Position.Left} id={`${option.id}-target`}/>*!/*/}
      {/*          <IconButton onClick={() => {*/}
      {/*            updateData({*/}
      {/*              force_update_id: id,*/}
      {/*              options: [...data.options.map((op: any) => {*/}
      {/*                if(op.id === option.id) {*/}
      {/*                  op.enabled = !op.enabled;*/}
      {/*                }*/}
      {/*                return op;*/}
      {/*              })]*/}
      {/*            });*/}
      {/*          }} disabled={!data.enabled}>*/}
      {/*            {data.enabled && option.enabled ? <CheckCircle color={'success'}/> : <DoNotDisturb color={'error'} />}*/}
      {/*          </IconButton>*/}
      {/*          <Button*/}
      {/*            disabled={!data.enabled || !option.enabled || useCase}*/}
      {/*            variant="outlined"*/}
      {/*            sx={Styles.btn}*/}
      {/*            onClick={() => {*/}
      {/*              onBtnClick('assetNode', option);*/}
      {/*            }}*/}
      {/*          >*/}
      {/*            {option.component_name}*/}
      {/*          </Button>*/}
      {/*          /!* {data.handles && (*/}
      {/*          <Handle type="source" p osition={Position.Right} {...(data.handles && { id: data.handles[i] })} />*/}
      {/*        )} *!/*/}
      {/*          {type === 'assetNode' && <Handle type="source" position={Position.Right} id={`${option.id}-source`}/>}*/}
      {/*        </Box>*/}
      {/*      )*/}
      {/*    )}*/}
      {/*    {!useCase && <Box sx={Styles.btnsWrapper}>*/}
      {/*      <Button*/}
      {/*        disabled={!data.enabled}*/}
      {/*        variant="outlined"*/}
      {/*        sx={Styles.btn}*/}
      {/*        startIcon={<AddCircleOutlineRounded/>}*/}
      {/*        onClick={() => {*/}
      {/*          onBtnClick('assetNode', {label: 'Add component', type: 'add_service'})*/}
      {/*        }}*/}
      {/*      >*/}
      {/*        Add component*/}
      {/*      </Button>*/}
      {/*    </Box>}*/}
      {/*    /!*<Button variant="contained" onClick={() => onBtnClick('assetNode', {label: 'Add service', type: 'add_service'})} startIcon={<AddCircleOutlineRounded />} sx={Styles.scanBtn}>*!/*/}
      {/*    /!*  Add service*!/*/}
      {/*    /!*</Button>*!/*/}
      {/*    /!*<Button variant="contained" startIcon={<ScanIcon />} sx={Styles.scanBtn}>*!/*/}
      {/*    /!*  Scan Services*!/*/}
      {/*    /!*</Button>*!/*/}
      {/*  </Box>*/}
      {/*</Box>*/}
      {/* {data.handles && <Handle type="source" position={Position.Right} style={{ top: '125px' }} />} */}
    </>
  );
};
